/**
 * Assessment milestone ladder (orchestrator RFC §4.6): day-1 Day sanity run →
 * day-7 Week run → day-30 Month run, anchored on MigrateContext.discoveryStartedAt.
 * Milestones are nudges — runs are never auto-created.
 */

export interface MilestoneDue {
  /** Ladder rung identifier. */
  milestone: 'day-1' | 'week-1' | 'month-1';
  /** Azure Migrate assessment timeRange the rung calls for. */
  timeRange: 'Day' | 'Week' | 'Month';
  /** When the rung's perf-data window matures. */
  dueAt: Date;
  /** True once the window has matured — the run can be executed now. */
  ready: boolean;
}

const LADDER: { milestone: MilestoneDue['milestone']; timeRange: MilestoneDue['timeRange']; days: number }[] = [
  { milestone: 'day-1', timeRange: 'Day', days: 1 },
  { milestone: 'week-1', timeRange: 'Week', days: 7 },
  { milestone: 'month-1', timeRange: 'Month', days: 30 },
];

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The milestone the architect should act on: the latest rung whose window has
 * matured (ready), otherwise the first upcoming rung. The month rung stays ready
 * forever — month-window runs are the steady state once discovery age passes 30
 * days. Returns null when discovery has not started (no anchor, no nudge).
 */
export function nextRunDue(
  discoveryStartedAt: string | null | undefined,
  now: Date = new Date(),
): MilestoneDue | null {
  if (!discoveryStartedAt) return null;
  const anchor = new Date(discoveryStartedAt);
  if (Number.isNaN(anchor.getTime())) return null;

  const rungs = LADDER.map(r => ({
    milestone: r.milestone,
    timeRange: r.timeRange,
    dueAt: new Date(anchor.getTime() + r.days * DAY_MS),
    ready: now.getTime() >= anchor.getTime() + r.days * DAY_MS,
  }));

  const matured = rungs.filter(r => r.ready);
  if (matured.length > 0) return matured[matured.length - 1];
  return rungs[0];
}

/**
 * Frontend mirror of the backend CommitmentPricingGate (RFC §4.6) — window AND stars,
 * never stars alone: RI / Savings-Plan figures are eligible only from a Month-window run
 * (perfDataDays ≥ 28 — the run document does not persist timeRange) with discovery age
 * ≥ 30 days AND ≥ 4★ confidence. Keep in lockstep with
 * backend Services/Estate/CommitmentPricingGate.cs.
 */
export const MONTH_WINDOW_MIN_PERF_DAYS = 28;
export const MIN_DISCOVERY_AGE_DAYS = 30;
export const MIN_CONFIDENCE_STARS = 4;

export interface GateRunInput {
  runNumber: number;
  kind: string;
  status: string;
  perfDataDays?: number | null;
  confidenceStars?: number | null;
}

export interface GateStatus {
  eligible: boolean;
  reason: 'no-anchor' | 'maturing' | 'no-month-run' | 'low-confidence' | 'met';
  pendingUntil?: Date;
  qualifyingRunNumber?: number;
}

export function commitmentPricingGate(
  runs: GateRunInput[],
  discoveryStartedAt: string | null | undefined,
  now: Date = new Date(),
): GateStatus {
  if (!discoveryStartedAt) return { eligible: false, reason: 'no-anchor' };
  const anchor = new Date(discoveryStartedAt);
  if (Number.isNaN(anchor.getTime())) return { eligible: false, reason: 'no-anchor' };

  const maturesAt = new Date(anchor.getTime() + MIN_DISCOVERY_AGE_DAYS * DAY_MS);
  if (now.getTime() < maturesAt.getTime()) {
    return { eligible: false, reason: 'maturing', pendingUntil: maturesAt };
  }

  const monthRuns = runs
    .filter(r => r.kind === 'machine-assessment' && r.status === 'imported'
      && (r.perfDataDays ?? 0) >= MONTH_WINDOW_MIN_PERF_DAYS)
    .sort((a, b) => b.runNumber - a.runNumber);

  if (monthRuns.length === 0) return { eligible: false, reason: 'no-month-run' };

  const qualifying = monthRuns.find(r => (r.confidenceStars ?? 0) >= MIN_CONFIDENCE_STARS);
  if (!qualifying) return { eligible: false, reason: 'low-confidence' };

  return { eligible: true, reason: 'met', qualifyingRunNumber: qualifying.runNumber };
}
